import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { LoadingSpinner } from './components/LoadingStates';
import Layout from './components/Layout';
import AuthGate from './components/AuthGate';
import { ProtectedRoute } from './components/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import PrestazioniMese from './pages/PrestazioniMese';
import Copywriter from './pages/Copywriter';
import AdminAssignments from './pages/AdminAssignments';
import Calendar from './pages/Calendar';
import CalendarioEditoriale from './pages/CalendarioEditoriale';
import ClientCalendar from './pages/ClientCalendar';
import Clienti from './pages/Clienti';
import NewCalendar from './pages/NewCalendar';
import Profile from './pages/Profile';
import Programmazione from './pages/Programmazione';
import Videomaker from './pages/Videomaker';
import Editing from './pages/Editing';
import VideomakerContenuto from './pages/VideomakerContenuto';
import VideomakerAppuntamenti from './pages/VideomakerAppuntamenti';
import Appuntamenti from './pages/Appuntamenti';
import RemindersApp from './pages/RemindersApp';
import LastRouteRedirect from './components/LastRouteRedirect';
import { DataProvider } from './contexts/DataContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import './App.css';

const AppContent: React.FC = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center relative overflow-hidden"
        style={{ backgroundColor: '#FAFAFB' }}
        role="status"
        aria-label="Loading application"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(19,22,28,0.03) 0%, transparent 60%)',
          }}
          aria-hidden="true"
        />
        <div className="text-center space-y-6 relative">
          <img
            src="/bymarke-wordmark-white.png"
            alt="ByMarke"
            className="h-7 w-auto mx-auto select-none"
            style={{ filter: 'brightness(0)' }}
            draggable={false}
          />
          <div className="space-y-3">
            <div className="flex justify-center space-x-1.5">
              <div
                className="w-1.5 h-1.5 rounded-full animate-bounce"
                style={{ animationDelay: '0s', backgroundColor: '#13161C' }}
              ></div>
              <div
                className="w-1.5 h-1.5 rounded-full animate-bounce"
                style={{ animationDelay: '0.15s', backgroundColor: '#13161C' }}
              ></div>
              <div
                className="w-1.5 h-1.5 rounded-full animate-bounce"
                style={{ animationDelay: '0.3s', backgroundColor: '#13161C' }}
              ></div>
            </div>
            <p
              className="text-xs uppercase tracking-[0.2em] font-medium"
              style={{ color: '#8B92A0' }}
            >
              Caricamento
            </p>
          </div>
        </div>
        <span className="sr-only">Loading application, please wait...</span>
      </div>
    );
  }

  if (!user) {
    return <AuthGate />;
  }

  return (
    <Layout>
      <Routes>
        {/* Dashboard route - default page */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />

        {/* Analisi: prestazioni del mese e tempi medi di lavoro (voce in Configurazione) */}
        <Route
          path="/analisi"
          element={
            <ProtectedRoute>
              <PrestazioniMese />
            </ProtectedRoute>
          }
        />
        {/* Vecchio indirizzo di "Maggiori dettagli" (link salvati) */}
        <Route path="/prestazioni-mese" element={<Navigate to="/analisi" replace />} />

        {/* Copywriter page */}
        <Route
          path="/copywriter"
          element={
            <ProtectedRoute>
              <Copywriter />
            </ProtectedRoute>
          }
        />

        {/* Admin Assignments page */}
        <Route
          path="/admin-assignments"
          element={
            <ProtectedRoute>
              <AdminAssignments />
            </ProtectedRoute>
          }
        />


        {/* Calendar page */}
        <Route
          path="/calendar"
          element={
            <ProtectedRoute>
              <Calendar />
            </ProtectedRoute>
          }
        />

        {/* Calendario Editoriale page */}
        <Route
          path="/calendario-editoriale"
          element={
            <ProtectedRoute>
              <CalendarioEditoriale />
            </ProtectedRoute>
          }
        />

        {/* Client Calendar page */}
        <Route
          path="/client-calendar"
          element={
            <ProtectedRoute>
              <ClientCalendar />
            </ProtectedRoute>
          }
        />

        {/* Clienti page */}
        <Route
          path="/clienti"
          element={
            <ProtectedRoute>
              <Clienti />
            </ProtectedRoute>
          }
        />

        {/* New Calendar page */}
        <Route
          path="/new-calendar"
          element={
            <ProtectedRoute>
              <NewCalendar />
            </ProtectedRoute>
          }
        />

        {/* Profile page */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />

        {/* Programmazione page */}
        <Route
          path="/publisher"
          element={
            <ProtectedRoute>
              <Programmazione />
            </ProtectedRoute>
          }
        />



        {/* Videomaker page */}
        <Route
          path="/videomaker"
          element={
            <ProtectedRoute>
              <Videomaker />
            </ProtectedRoute>
          }
        />

        {/* Editing: registrati da montare (tra Videomaker e Programmazione) */}
        <Route
          path="/editing"
          element={
            <ProtectedRoute>
              <Editing />
            </ProtectedRoute>
          }
        />

        {/* Videomaker - dettaglio contenuto (pagina, non popup) */}
        <Route
          path="/videomaker/contenuto/:id"
          element={
            <ProtectedRoute>
              <VideomakerContenuto />
            </ProtectedRoute>
          }
        />

        {/* Videomaker - calendario appuntamenti (iCloud live) */}
        <Route
          path="/videomaker/appuntamenti"
          element={
            <ProtectedRoute>
              <VideomakerAppuntamenti />
            </ProtectedRoute>
          }
        />

        {/* Appuntamenti iCloud (non ancora in menu - raggiungibile via /appuntamenti) */}
        <Route
          path="/appuntamenti"
          element={
            <ProtectedRoute>
              <Appuntamenti />
            </ProtectedRoute>
          }
        />

        {/* La radice riporta all'ultima pagina visitata (fallback: dashboard) */}
        <Route path="/" element={<LastRouteRedirect />} />

        {/* Redirect all other routes to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <DataProvider>
          <Router>
            <Routes>
              {/* Public route for client calendar - no auth required */}
              <Route
                path="/calendar/client/:clientId"
                element={<ClientCalendar />}
              />
              {/* Area Promemoria: app autonoma con login proprio, fuori dalla dashboard */}
              <Route path="/promemoria" element={<RemindersApp />} />
              {/* All other routes handled by AppContent */}
              <Route path="*" element={<AppContent />} />
            </Routes>
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 3000,
                style: {
                  background: '#FFFFFF',
                  color: '#13161C',
                  border: '1px solid #DDE0E6',
                  boxShadow: '0 8px 24px rgba(19,22,28,0.08)',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: 500,
                  letterSpacing: '0',
                  padding: '12px 16px',
                },
                success: {
                  iconTheme: { primary: '#16A34A', secondary: '#FFFFFF' },
                  style: {
                    border: '1px solid rgba(22, 163, 74, 0.35)',
                  },
                },
                error: {
                  iconTheme: { primary: '#DC2626', secondary: '#FFFFFF' },
                  style: {
                    border: '1px solid rgba(220, 38, 38, 0.35)',
                  },
                },
              }}
            />
          </Router>
        </DataProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
